import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../api/index.js'
import { useNavigate } from 'react-router-dom'
import TenantBrandEditor from '../modules/admin/components/TenantBrandEditor.jsx'
import TenantModules from '../modules/admin/components/TenantModules.jsx'
import TenantAddOns from '../modules/admin/components/TenantAddOns.jsx'
import TenantOfficeWorker from '../modules/admin/components/TenantOfficeWorker.jsx'
import PlatformActivity from '../modules/admin/components/PlatformActivity.jsx'
import ChangeMyPassword from './ChangeMyPassword.jsx'
import { useAuth } from '../hooks/useAuth.jsx'
import { identityService } from '../shared/api'
import { signOutStorage } from '../shared/auth/signOut'
import { asMessage } from '../shared/utils/errorDetail'
import { platformEnvironment } from '../shared/utils/host'

// Every refusal the server sends becomes TEXT before it reaches the page. A 422's `detail` is a LIST of
// objects (an admin email like "a@b" passes the browser's check and fails the server's), and putting
// that straight into JSX is React error #31: the whole console goes blank instead of saying what was
// wrong. shared/utils/errorDetail.asMessage turns any detail into a sentence.
const errorText = (err, fallback) => asMessage(err?.response?.data?.detail) || err?.message || fallback

// The same rule the server applies (identity services/password_service.py is_strong_password), said
// on the form so nobody has to be refused to learn it.
const PASSWORD_RULE = 'At least 8 characters, with an upper-case letter, a lower-case letter, a number '
  + 'and a special character (such as ! @ # $ %).'

// Which environment, as plainly as possible: a platform admin must never mistake production for staging.
const ENVIRONMENT = {
  production: { label: 'PRODUCTION', className: 'bg-red-600 text-white' },
  staging: { label: 'STAGING', className: 'bg-amber-400 text-slate-900' },
  local: { label: 'LOCAL', className: 'bg-slate-600 text-white' },
  // A host that is not known to be either (a company's own domain): no badge - the host and version
  // below still say where this is, and no guess is made (review SA-3).
  unknown: null,
}
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '2.180.3'


/**
 * Whether a tenant is actually finished being set up, and the one-click fix for the common miss.
 *
 * Provisioning is a button plus several follow-up steps, and each of them fails SILENTLY: the app
 * keeps working, mail keeps sending, and the only symptom is a customer who does not recognise the
 * sender or never receives paperwork they signed. On 2026-08-17 that cost a day; on 2026-08-19 five
 * signed tickets reached nobody. Neither was visible on any screen — which is what this column is.
 *
 * WARN vs INFO is deliberate. Only things that make the product quietly WRONG for a customer raise
 * the amber flag; a missing logo is untidy and sits below it. Treating them alike is how people
 * learn to ignore a warning.
 */
function TenantSetup({ tenant, onFixSender, busy, onCheck, checking, checkResult,
                      onSetupDomain, settingUp, setupResult }) {
  const [open, setOpen] = useState(false)
  const s = tenant.setup
  if (!s) return <span className="text-xs text-gray-400">—</span>

  const senderCheck = s.checks.find(c => c.key === 'sending_address')
  const senderNeedsFix = senderCheck && !senderCheck.ok

  return (
    <div className="text-xs">
      <button onClick={() => setOpen(o => !o)}
              className={`font-semibold ${s.needs_attention ? 'text-amber-700' : 'text-green-700'}`}>
        {s.needs_attention ? `⚠ ${s.summary}` : `✓ ${s.done}/${s.total}`}
      </button>

      {open && (
        <ul className="mt-2 space-y-1.5">
          {s.checks.map(c => (
            <li key={c.key} className="max-w-xs">
              <span className={c.ok ? 'text-green-700' : c.severity === 'warn'
                ? 'text-amber-800' : 'text-gray-500'}>
                {c.ok ? '✓' : c.severity === 'warn' ? '⚠' : '•'} {c.label}
              </span>
              {c.detail && <span className="block text-gray-500">{c.detail}</span>}
              {c.fix && <span className="block text-gray-400">{c.fix}</span>}
            </li>
          ))}
          <li className="pt-1">
            {/* The database can only say an address is CONFIGURED. Whether that domain signs
                anything is a fact only SES holds, and the two diverge exactly when it matters. */}
            <button onClick={onCheck} disabled={checking}
                    className="rounded border border-gray-300 bg-white px-2 py-1 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {checking ? 'Checking…' : 'Check sending domain'}
            </button>
            {/* `inherited` is green on purpose: a subdomain sending under a verified parent is a
                working, signed configuration, not a lesser one. Colouring it amber would send
                somebody to "fix" a tenant that needs nothing. */}
            {checkResult && (
              <span className={`mt-1 block ${
                ['verified', 'inherited'].includes(checkResult.state) ? 'text-green-700'
                  : checkResult.state === 'unknown' ? 'text-gray-500'
                  : checkResult.state === 'pending' ? 'text-amber-700' : 'text-red-700'}`}>
                {checkResult.summary}
                {(checkResult.detail || []).map((d, i) => (
                  <span key={i} className="block text-gray-500">{d}</span>
                ))}
              </span>
            )}
          </li>
          {tenant.custom_domain && (
            <li className="pt-1">
              {/* Only for a tenant on their OWN domain. A subdomain tenant already sends under the
                  platform's identity, and writing any DNS record at their name would stop the
                  wildcard answering for it and take their portal offline. */}
              <button onClick={() => onSetupDomain(false)} disabled={settingUp}
                      className="rounded border border-gray-300 bg-white px-2 py-1 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {settingUp ? 'Working…' : 'Set up sending domain'}
              </button>

              {setupResult && (
                <div className="mt-1">
                  {/* `applied` is NOT green. Green belongs to Check sending domain, which asks SES
                      to resolve these records from the public internet — that is the only thing
                      that proves they landed somewhere the world can see. */}
                  <span className={
                    setupResult.state === 'refused' ? 'block text-gray-600'
                      : setupResult.state === 'unknown' ? 'block text-red-700'
                      : 'block text-amber-800'}>
                    {setupResult.summary}
                  </span>

                  {(setupResult.warnings || []).map((w, i) => (
                    <span key={i} className="mt-1 block text-amber-800">⚠ {w}</span>
                  ))}

                  {(setupResult.written || []).length > 0 && (
                    <span className="mt-1 block text-gray-500">
                      {setupResult.state === 'planned' ? 'Would write:' : 'Wrote:'}{' '}
                      {setupResult.written.map(r => `${r.type} ${r.name}`).join(', ')}
                    </span>
                  )}
                  {(setupResult.skipped || []).length > 0 && (
                    <span className="mt-1 block text-gray-400">
                      Already correct: {setupResult.skipped.length} record(s)
                    </span>
                  )}
                  {(setupResult.manual || []).length > 0 && (
                    <span className="mt-1 block text-gray-600">
                      Needs a person: {setupResult.manual.map(r => r.reason || r.label).join(' ')}
                    </span>
                  )}

                  {/* The diff has been shown; applying it is a separate, differently worded press.
                      This is the only place in the product that edits a customer's live DNS. */}
                  {setupResult.state === 'planned'
                    && (setupResult.written || []).length > 0 && (
                    <button onClick={() => onSetupDomain(true)} disabled={settingUp}
                            className="mt-1 rounded border border-amber-300 bg-white px-2 py-1 font-semibold text-amber-900 hover:bg-amber-50 disabled:opacity-50">
                      {settingUp ? 'Writing…' : `Write these ${setupResult.written.length} record(s)`}
                    </button>
                  )}

                  {(setupResult.next_steps || []).map((n, i) => (
                    <span key={i} className="mt-1 block text-gray-400">{n}</span>
                  ))}
                </div>
              )}
            </li>
          )}
          {senderNeedsFix && (
            <li>
              <button onClick={onFixSender} disabled={busy}
                      className="mt-1 rounded border border-amber-300 bg-white px-2 py-1 font-semibold text-amber-900 hover:bg-amber-50 disabled:opacity-50">
                {busy ? 'Setting…' : 'Use their own sending address'}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

export default function SuperAdminDashboard() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { logout } = useAuth()
  // Only the company list: react-query v5 reads a bare array as filters it does not understand, and
  // refetches EVERY query on the page.
  const refreshTenants = () => queryClient.invalidateQueries({ queryKey: ['tenants'] })
  const environment = ENVIRONMENT[platformEnvironment()] ?? null
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  const [showModal, setShowModal] = useState(false)
  const [confirmToggle, setConfirmToggle] = useState(null) // { tenant, newStatus }
  const [editingBrandTenant, setEditingBrandTenant] = useState(null)
  // Which held modules (Capital, Capex, Invoicing) a company has — the platform's decision, not the
  // company's, so it lives here and not on the company's own admin screen.
  const [editingModulesTenant, setEditingModulesTenant] = useState(null)
  // Unsaved ticks on the Modules screen: closing asks first, rather than throwing them away (review APP-8).
  const [modulesDirty, setModulesDirty] = useState(false)
  const closeModules = () => {
    if (modulesDirty && !window.confirm('You have module changes that are not saved. Close without saving them?')) return
    setModulesDirty(false)
    setEditingModulesTenant(null)
  }
  // A company's paid add-ons (identity /branding/{id}/features), also the platform's decision.
  const [editingAddOnsTenant, setEditingAddOnsTenant] = useState(null)
  // A company's AI Office Worker: a guided set-up checklist and its connection keys (identity /platform/office-worker/{id}).
  // Replaces scripts/agent_host_admin.py for normal use. A newly issued key lives only inside the
  // panel; closing while it is still on screen asks first, because it is never shown again. And while
  // a change is being sent the panel does not close at all: a new key answered after the panel
  // had gone would be a live key that nobody ever saw (security review of b9958597).
  const [editingOfficeWorkerTenant, setEditingOfficeWorkerTenant] = useState(null)
  const [officeWorkerKeyShowing, setOfficeWorkerKeyShowing] = useState(false)
  const [officeWorkerBusy, setOfficeWorkerBusy] = useState(false)
  const closeOfficeWorker = () => {
    if (officeWorkerBusy) return
    if (officeWorkerKeyShowing && !window.confirm('The new key is still on screen and will never be shown again. Close anyway?')) return
    setOfficeWorkerKeyShowing(false)
    setOfficeWorkerBusy(false)
    setEditingOfficeWorkerTenant(null)
  }
  // The portal had no account controls at all. A password somebody else issued, that its
  // holder cannot rotate, is a credential nobody can take back.
  const [tab, setTab] = useState('tenants')

  // Sends nothing: the server derives the address from the tenant's own host. A free-text box here
  // would be one more place to mistype the thing whose being wrong is invisible.
  // Asks SES, so it is a button rather than part of the page load: a tenant list that failed to
  // render because SES was throttled would be a worse page than one that says "not checked".
  const [checked, setChecked] = useState({})   // tenantId -> result
  const checkDomain = useMutation({
    mutationFn: (tenantId) =>
      apiClient.post(`/tenants/${tenantId}/check-sending-domain`)
        .then(r => ({ tenantId, result: r.data })),
    onSuccess: ({ tenantId, result }) => setChecked(c => ({ ...c, [tenantId]: result })),
    onError: (e) => setError(errorText(e, 'Could not check the sending domain.')),
  })

  // Writes to a customer's live DNS, so `apply` is opt-in and the first press only plans. The
  // result is kept per tenant: a shared slot would show one row's plan under another row's button,
  // which on an action like this is worse than showing nothing.
  const [setupPlans, setSetupPlans] = useState({})   // tenantId -> result
  const setupDomain = useMutation({
    mutationFn: ({ tenantId, apply }) =>
      apiClient.post(`/tenants/${tenantId}/setup-sending-domain`, { apply })
        .then(r => ({ tenantId, result: r.data })),
    onSuccess: ({ tenantId, result }) => {
      setSetupPlans(p => ({ ...p, [tenantId]: result }))
      // A real run changes what the checklist reports, so re-read the tenants.
      if (result.state !== 'planned') refreshTenants()
    },
    onError: (e) => setError(errorText(e, 'Could not set up the sending domain.')),
  })

  const fixSender = useMutation({
    mutationFn: (tenantId) => apiClient.post(`/tenants/${tenantId}/sending-address`, {}),
    onSuccess: () => refreshTenants(),
    onError: (e) => setError(errorText(e, 'Could not set the sending address.')),
  })
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    custom_domain: '',
    primary_color: '#334155',
    secondary_color: '#475569',
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_password: ''
  })
  const [error, setError] = useState('')

  const { data: tenants, isLoading, error: tenantsError } = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      // Trailing slash is required: FastAPI route is /tenants/ (prefix + "/")
      // Without it, FastAPI 307-redirects to /tenants/, axios follows,
      // and nginx serves the SPA instead of the backend.
      const res = await apiClient.get('/tenants/')
      const raw = res.data
      if (Array.isArray(raw)) return raw
      if (raw && Array.isArray(raw.tenants)) return raw.tenants
      if (raw && Array.isArray(raw.items)) return raw.items
      console.warn('[SuperAdmin] Unexpected tenants response shape:', raw)
      return []
    }
  })

  const createTenant = useMutation({
    mutationFn: async (data) => {
      // Trailing slash matches FastAPI route exactly: POST /tenants/
      const res = await apiClient.post('/tenants/', data)
      return res.data
    },
    onSuccess: () => {
      refreshTenants()
      setShowModal(false)
      setFormData({
        name: '', subdomain: '', custom_domain: '', primary_color: '#334155',
        secondary_color: '#475569', admin_first_name: '', admin_last_name: '',
        admin_email: '', admin_password: ''
      })
      setError('')
    },
    onError: (err) => {
      setError(errorText(err, 'Failed to create tenant'))
    }
  })

  const toggleTenantStatus = useMutation({
    mutationFn: async ({ tenantId, isActive }) => {
      const res = await apiClient.patch(`/tenants/${tenantId}`, { is_active: isActive })
      return res.data
    },
    onSuccess: () => {
      refreshTenants()
      setConfirmToggle(null)
    },
    onError: (err) => {
      setError(errorText(err, 'Failed to update tenant status'))
      setConfirmToggle(null)
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    createTenant.mutate(formData)
  }

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value })

  // THE APP'S REAL SIGN-OUT. This button used to remove a localStorage key called 'token' that nothing
  // uses and go to /login: the session - the in-memory access token and the refresh cookie - survived,
  // so Back, or the next person at the machine, was still a platform admin.
  //
  // THE SERVER IS ALWAYS ASKED (review SA-2). useAuth().logout() calls POST /auth/logout only while an
  // access token is in memory, and in a busy minute there may be none while the refresh cookie is still
  // good - then only the browser was cleared and the next request minted a new platform-admin token
  // from the surviving cookie. identityService.logout() goes through the shared client, which mints a
  // token from the cookie first, so the server clears the cookie. Then the browser is cleared the way
  // the app header's Logout does it, and useAuth forgets the user (its own server call is skipped: the
  // token is already gone). Leaving never waits on the server answering.
  const signOut = async () => {
    try {
      await identityService.logout()
    } catch (_) {
      // Unreachable: the browser is cleared below regardless.
    }
    signOutStorage()
    try {
      await logout()
    } catch (_) {
      // nothing left to do
    }
    navigate('/login', { replace: true })
  }

  const tabButton = (id, label) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      aria-current={tab === id ? 'page' : undefined}
      className={`text-sm font-medium transition-colors ${tab === id ? 'text-white underline underline-offset-4' : 'text-slate-300 hover:text-white'}`}
    >
      {label}
    </button>
  )

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <header className="bg-slate-900 text-white shadow-md py-4 px-4 sm:px-6 flex flex-wrap gap-3 justify-between items-center">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <h1 className="text-xl font-bold tracking-tight">System Admin Portal</h1>
          {/* WHICH ENVIRONMENT AND WHICH RELEASE, always on screen. The same bundle runs on staging and
              production, so a platform admin switching a company's modules must be able to see at a
              glance which one this is - and the host it was worked out from. */}
          {environment && (
            <span data-testid="environment-badge"
                  className={`rounded px-2 py-0.5 text-xs font-bold tracking-wider ${environment.className}`}>
              {environment.label}
            </span>
          )}
          <span className="text-xs text-slate-300" data-testid="environment-detail">
            {host} · v{APP_VERSION}
          </span>
        </div>
        <nav className="flex flex-wrap items-center gap-4">
          {tabButton('tenants', 'Companies')}
          {tabButton('activity', 'Activity')}
          {tabButton('account', 'My account')}
          <button
            type="button"
            onClick={signOut}
            className="text-sm font-medium hover:text-red-400 transition-colors"
          >
            Logout
          </button>
        </nav>
      </header>
      
      <main className="p-6 max-w-7xl mx-auto space-y-6">
        {tab === 'account' && (
          <div className="max-w-2xl">
            <ChangeMyPassword />
          </div>
        )}

        {tab === 'activity' && <PlatformActivity tenants={Array.isArray(tenants) ? tenants : []} />}

        {tab === 'tenants' && (<>
        {/* Until now `error` was only ever rendered INSIDE the two modals, so a failure from any
            row-level action on this page landed nowhere at all. That is a poor property for any
            screen and an absurd one for this screen, whose entire reason for existing is that
            silent failures in tenant setup cost a day. */}
        {error && (
          <div className="flex items-start justify-between gap-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <span>{error}</span>
            <button onClick={() => setError('')}
                    className="font-semibold text-red-900 hover:underline">Dismiss</button>
          </div>
        )}
        <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Client Tenants</h2>
            <p className="text-gray-500 text-sm mt-1">Manage isolated client environments</p>
          </div>
          <button 
            onClick={() => setShowModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center shadow-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
            Provision New Client
          </button>
        </div>

        {/* Scrolls sideways on a narrow screen instead of cutting the last columns (Suspend among them) off. */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto" data-testid="tenants-table-scroller">
          <table className="w-full min-w-[56rem] text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-sm font-semibold uppercase tracking-wider border-b border-gray-200">
                <th className="p-4">Client Name</th>
                <th className="p-4">Subdomain</th>
                <th className="p-4">Custom Domain</th>
                <th className="p-4">Status</th>
                {/* "Set up" answers the question nobody had a way to ask: is this tenant FINISHED?
                    Provisioning is one button and several follow-up steps, and every one of them
                    fails silently — a tenant left sending as the platform address went unnoticed
                    for weeks because no screen could show it. */}
                <th className="p-4">Set up</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500">Loading tenants...</td></tr>
              ) : tenantsError ? (
                <tr><td colSpan="6" className="p-8 text-center text-red-500">
                  Failed to load tenants: {errorText(tenantsError, 'no answer')}
                </td></tr>
              ) : !Array.isArray(tenants) || tenants.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-500">No tenants found.</td></tr>
              ) : (
                tenants.map(tenant => (
                  <tr key={tenant.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 font-medium text-gray-900">{tenant.name}</td>
                    <td className="p-4 text-gray-600">{tenant.subdomain}</td>
                    <td className="p-4 text-gray-600">{tenant.custom_domain || '-'}</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        tenant.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {tenant.is_active ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td className="p-4">
                      <TenantSetup
                        tenant={tenant}
                        onFixSender={() => fixSender.mutate(tenant.id)}
                        busy={fixSender.isPending && fixSender.variables === tenant.id}
                        onCheck={() => checkDomain.mutate(tenant.id)}
                        checking={checkDomain.isPending && checkDomain.variables === tenant.id}
                        checkResult={checked[tenant.id]}
                        onSetupDomain={(apply) =>
                          setupDomain.mutate({ tenantId: tenant.id, apply })}
                        settingUp={setupDomain.isPending
                          && setupDomain.variables?.tenantId === tenant.id}
                        setupResult={setupPlans[tenant.id]}
                      />
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingBrandTenant(tenant)}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          Branding
                        </button>
                        <button
                          onClick={() => setEditingModulesTenant(tenant)}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          Modules
                        </button>
                        <button
                          onClick={() => setEditingAddOnsTenant(tenant)}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          Add-ons
                        </button>
                        <button
                          onClick={() => setEditingOfficeWorkerTenant(tenant)}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          Office Worker
                        </button>
                        <button
                          onClick={() => setConfirmToggle({ tenant, newStatus: !tenant.is_active })}
                          className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                            tenant.is_active
                              ? 'border-red-200 text-red-600 hover:bg-red-50'
                              : 'border-green-200 text-green-700 hover:bg-green-50'
                          }`}
                        >
                          {tenant.is_active ? 'Suspend' : 'Reactivate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </>)}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-xl font-bold text-gray-800">Provision New Client</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto">
                {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-6">{error}</div>}
                
                <form id="tenantForm" onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Organization Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                        <input required type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain</label>
                        <input required type="text" name="subdomain" value={formData.subdomain} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" placeholder="e.g. acme" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Custom Domain (Optional)</label>
                        <input type="text" name="custom_domain" value={formData.custom_domain} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" placeholder="e.g. acme.com" />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-6">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Branding Colors</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                        <div className="flex space-x-2">
                          <input type="color" name="primary_color" value={formData.primary_color} onChange={handleChange} className="h-10 w-10 border-0 p-0 rounded cursor-pointer" />
                          <input type="text" name="primary_color" value={formData.primary_color} onChange={handleChange} className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 outline-none font-mono" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                        <div className="flex space-x-2">
                          <input type="color" name="secondary_color" value={formData.secondary_color} onChange={handleChange} className="h-10 w-10 border-0 p-0 rounded cursor-pointer" />
                          <input type="text" name="secondary_color" value={formData.secondary_color} onChange={handleChange} className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 outline-none font-mono" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-6">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Admin Account</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                        <input required type="text" name="admin_first_name" value={formData.admin_first_name} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                        <input required type="text" name="admin_last_name" value={formData.admin_last_name} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input required type="email" name="admin_email" value={formData.admin_email} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <input required type="password" name="admin_password" value={formData.admin_password} onChange={handleChange} aria-describedby="admin-password-rule" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 outline-none" />
                        <p id="admin-password-rule" className="mt-1 text-xs text-gray-500">{PASSWORD_RULE}</p>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3 sticky bottom-0">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors">Cancel</button>
                <button form="tenantForm" type="submit" disabled={createTenant.isPending} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50">
                  {createTenant.isPending ? 'Provisioning...' : 'Create Tenant'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Suspend / Reactivate confirmation dialog */}
        {confirmToggle && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                {confirmToggle.newStatus ? 'Reactivate Client?' : 'Suspend Client?'}
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                {/* Says everything that stops, not only logins: since the agent host checks the company's
                    status, a suspended company's AI Office Worker stops too (reads, notices and customer
                    emails). Nothing is deleted either way. */}
                {confirmToggle.newStatus
                  ? `"${confirmToggle.tenant.name}" will be reactivated. Their people can log in again straight away, and their AI Office Worker (if they have one) works again.`
                  : `"${confirmToggle.tenant.name}" will be suspended. Every login at this company is blocked, and its AI Office Worker stops: no reading, no notices, no emails to customers. Nothing is deleted; Reactivate undoes it.`
                }
              </p>
              {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">{error}</div>}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setConfirmToggle(null); setError('') }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => toggleTenantStatus.mutate({
                    tenantId: confirmToggle.tenant.id,
                    isActive: confirmToggle.newStatus
                  })}
                  disabled={toggleTenantStatus.isPending}
                  className={`px-5 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50 ${
                    confirmToggle.newStatus
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {toggleTenantStatus.isPending
                    ? 'Processing...'
                    : confirmToggle.newStatus ? 'Yes, Reactivate' : 'Yes, Suspend'
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Per-tenant branding editor (system_admin → any tenant) */}
        {editingBrandTenant && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-xl font-bold text-gray-800">Branding — {editingBrandTenant.name}</h3>
                <button onClick={() => setEditingBrandTenant(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                <TenantBrandEditor
                  tenantId={editingBrandTenant.id}
                  mode="super"
                  onSaved={refreshTenants}
                />
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end sticky bottom-0">
                <button onClick={() => setEditingBrandTenant(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors">Done</button>
              </div>
            </div>
          </div>
        )}

        {/* Paid add-ons per company (system_admin; audited server-side) */}
        {editingAddOnsTenant && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-xl font-bold text-gray-800">Add-ons — {editingAddOnsTenant.name}</h3>
                <button onClick={() => setEditingAddOnsTenant(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                <TenantAddOns tenantId={editingAddOnsTenant.id} />
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end sticky bottom-0">
                <button onClick={() => setEditingAddOnsTenant(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors">Done</button>
              </div>
            </div>
          </div>
        )}

        {/* The AI Office Worker per company: guided set-up and connection keys (system_admin; audited server-side) */}
        {editingOfficeWorkerTenant && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-xl font-bold text-gray-800">AI Office Worker - {editingOfficeWorkerTenant.name}</h3>
                <button onClick={closeOfficeWorker} disabled={officeWorkerBusy} className="text-gray-400 hover:text-gray-600 disabled:opacity-50" aria-label="Close">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                <TenantOfficeWorker tenantId={editingOfficeWorkerTenant.id} onKeyShowingChange={setOfficeWorkerKeyShowing} onBusyChange={setOfficeWorkerBusy} />
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center gap-3 sticky bottom-0">
                {officeWorkerBusy && <span className="text-xs text-gray-500">Sending… the panel can close when this finishes.</span>}
                <button onClick={closeOfficeWorker} disabled={officeWorkerBusy} className="px-4 py-2 text-gray-700 hover:bg-gray-200 disabled:opacity-50 rounded-lg font-medium transition-colors">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Every module per company - standard (on unless switched off) and held (off until ticked); system_admin → any tenant; audited server-side */}
        {editingModulesTenant && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-xl font-bold text-gray-800">Modules — {editingModulesTenant.name}</h3>
                <button onClick={closeModules} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                <TenantModules tenantId={editingModulesTenant.id} onDirtyChange={setModulesDirty} />
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end sticky bottom-0">
                {/* "Close", not "Done": it closes; Save is what saves. */}
                <button onClick={closeModules} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors">Close</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
